import {useCallback, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import {PhoneHQLoader, PhoneHQButton, PhoneHQInput} from 'ch3-ui-lib';
import {ArrowDown, Search} from "lucide-react";
import {debounce} from "lodash";
import {useLazyListEnsAlertsQuery, useListEnsAlertsQuery} from "../../../store/features/ens/ensService";
import {EnsAlertStatus, EnsAlertSummary} from "../../../store/features/ens/types";
import {StatusBadge} from "../components/StatusBadge";
import {TypeBadge} from "../components/TypeBadge";
import {ConfirmedProgress} from "../components/ConfirmedProgress";
import {EnsPagination} from "../components/EnsPagination";
import {TargetCell} from "../components/TargetCell";
import {useEnsGroupNames} from "../useEnsGroupNames";
import {fetchAllAlertPages} from "./fetchAllAlertPages";
import {ENS_ALERT_REFRESH_INTERVAL_MS, TYPE_LABEL} from "../utils";

const PAGE_SIZE = 20;

const STATUS_LABEL: Record<EnsAlertStatus, string> = {
    PENDING: 'Pending',
    ESCALATING: 'Escalating',
    CONFIRMED: 'Confirmed',
    FAILED: 'Failed',
    CANCELLED: 'Cancelled',
};

const CSV_HEADERS = [
    'Alert ID', 'Name', 'Type', 'Status', 'Groups', 'Recipients',
    'Confirmed', 'Pending', 'Failed', 'Current level', 'Sent',
];

const escapeCsv = (value: string | number | null | undefined) => {
    const text = value == null ? '' : String(value);
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const groupsLabel = (targetGroupIds: number[] | undefined, groupNames: Map<number, string>) =>
    (targetGroupIds ?? []).map((id) => groupNames.get(id) ?? `#${id}`).join('; ');

const buildAlertsCsv = (rows: EnsAlertSummary[], groupNames: Map<number, string>) => {
    const lines = [CSV_HEADERS.join(',')];
    rows.forEach((alert) => {
        lines.push([
            alert.publicId,
            alert.name,
            TYPE_LABEL[alert.type],
            STATUS_LABEL[alert.status],
            groupsLabel(alert.targetGroupIds, groupNames),
            alert.targetCount,
            alert.confirmedCount,
            alert.pendingCount,
            alert.failedCount,
            alert.currentLevelSequence ?? '',
            alert.triggeredAt,
        ].map(escapeCsv).join(','));
    });
    return lines.join('\r\n');
};

const downloadCsv = (csv: string, filename: string) => {
    const blob = new Blob(['﻿' + csv], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const shortPublicId = (publicId: string) =>
    publicId.length > 8 ? `${publicId.slice(0, 8)}…` : publicId;

const formatTime = (iso: string) => {
    try {
        return new Date(iso).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
    } catch {
        return '—';
    }
};

const formatDate = (iso: string) => {
    try {
        return new Date(iso).toLocaleDateString([], {year: 'numeric', month: '2-digit', day: '2-digit'});
    } catch {
        return '';
    }
};

const COLUMNS = 'grid-cols-[120px_minmax(220px,1fr)_92px_130px_120px_minmax(150px,200px)_116px]';

export const EnsHistory = () => {
    const [phrase, setPhrase] = useState('');
    const [debouncedPhrase, setDebouncedPhrase] = useState('');
    const [page, setPage] = useState(0);

    const debouncePhrase = useMemo(
        () =>
            debounce((value: string) => {
                setDebouncedPhrase(value);
                setPage(0);
            }, 400),
        [],
    );

    const handlePhraseChange = useCallback(
        (value: string) => {
            setPhrase(value);
            debouncePhrase(value);
        },
        [debouncePhrase],
    );

    const {data, isLoading, isFetching} = useListEnsAlertsQuery({
        offset: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        phrase: debouncedPhrase || undefined,
    }, {pollingInterval: ENS_ALERT_REFRESH_INTERVAL_MS});

    const [fetchAllAlerts] = useLazyListEnsAlertsQuery();
    const [isExporting, setIsExporting] = useState(false);
    const groupNames = useEnsGroupNames();

    const alerts = data?.data ?? [];
    const total = data?.totalCount ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const handleExportCsv = async () => {
        if (total === 0 || isExporting) return;
        setIsExporting(true);
        try {
            const allAlerts = await fetchAllAlertPages((paging) => fetchAllAlerts({
                ...paging,
                phrase: debouncedPhrase || undefined,
            }).unwrap());
            if (!allAlerts.length) return;
            const stamp = new Date().toISOString().slice(0, 10);
            downloadCsv(buildAlertsCsv(allAlerts, groupNames), `ens-alert-history-${stamp}.csv`);
        } catch {
            // Failed export — RTK Query surfaces transport errors via the global handler.
        } finally {
            setIsExporting(false);
        }
    };
    const subhead = debouncedPhrase.length > 0
        ? `${total} ${total === 1 ? 'RESULT' : 'RESULTS'}`
        : `LAST 30 DAYS · ${total} ${total === 1 ? 'ALERT' : 'ALERTS'}`;

    return (
        <div className="py-6 flex flex-col gap-4">
            <div className="flex items-center gap-3 px-5 py-2.5">
                <div className="relative flex-1">
                    <PhoneHQInput
                        id="ensHistory-searchInput"
                        type="search"
                        value={phrase}
                        placeholder="Search by name, message or alert id"
                        className="w-full rounded-lg border-0 bg-zinc-100 py-2 pl-10 pr-9 text-tiny font-medium ring-0 placeholder:text-zinc-500 focus:bg-surface focus:ring-2 [&::-webkit-search-cancel-button]:appearance-none"
                        onChange={(e) => handlePhraseChange(e.target.value)}
                    />
                    <Search
                        size={18}
                        className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-zinc-500"
                    />
                </div>
                <PhoneHQButton
                    phq-id="ensHistory-exportCsvBtn"
                    variant="secondary"
                    size="sm"
                    onClick={handleExportCsv}
                    disabled={total === 0 || isExporting}
                >
                    <ArrowDown size={16} className={'!p-0'}/>
                    {isExporting ? 'Exporting…' : 'Export CSV'}
                </PhoneHQButton>
            </div>

            <div className="mx-5 overflow-hidden rounded border border-line bg-surface">
                <div className="px-4 py-3 border-b border-line text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
                    {subhead}
                </div>
                {isLoading && (
                    <div className="flex items-center justify-center py-10">
                        <PhoneHQLoader/>
                    </div>
                )}
                {!isLoading && alerts.length === 0 && (
                    <div className="px-4 py-10 text-center text-ink-muted text-sm">
                        No alerts match the current filters.
                    </div>
                )}
                {!isLoading && alerts.length > 0 && (
                    <div className="overflow-x-auto">
                        <div className="min-w-[980px]">
                            <div className={`grid ${COLUMNS} bg-surface-muted px-4 py-2 text-[11px] font-semibold uppercase text-ink-muted`}>
                                <div>ID</div>
                                <div>Alert</div>
                                <div>Type</div>
                                <div>Target</div>
                                <div>Status</div>
                                <div>Confirmed</div>
                                <div>Sent</div>
                            </div>
                            {alerts.map((alert) => (
                                <Link
                                    to={`/admin/ens/alerts/${alert.publicId}`}
                                    key={alert.publicId}
                                    className={`grid ${COLUMNS} items-center border-t border-line px-4 py-3 text-sm hover:bg-surface-muted`}
                                >
                                    <div className="font-mono text-[11px] text-ink-muted" title={alert.publicId}>
                                        {shortPublicId(alert.publicId)}
                                    </div>
                                    <div className="min-w-0 pr-4" title={alert.message}>
                                        <div className="truncate font-semibold text-ink">{alert.name}</div>
                                    </div>
                                    <div className="min-w-0">
                                        <TypeBadge type={alert.type}/>
                                    </div>
                                    <div className="min-w-0 pr-4">
                                        <TargetCell targetGroupIds={alert.targetGroupIds} targetCount={alert.targetCount} groupNames={groupNames}/>
                                    </div>
                                    <div className="min-w-0">
                                        <StatusBadge status={alert.status} variant="dot"/>
                                    </div>
                                    <div className="min-w-0 pr-4">
                                        <ConfirmedProgress
                                            confirmed={alert.confirmedCount}
                                            target={alert.targetCount}
                                            failed={alert.failedCount}
                                            pending={alert.pendingCount}
                                            compact
                                        />
                                    </div>
                                    <div className="text-[11px] text-ink-muted">
                                        <div>{formatTime(alert.triggeredAt)}</div>
                                        <div className="text-ink-muted">{formatDate(alert.triggeredAt)}</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {total > 0 && (
                <div className={'p-5'}>
                <EnsPagination
                    idPrefix="ensHistory"
                    page={page}
                    totalPages={totalPages}
                    isFetching={isFetching}
                    onChange={setPage}
                />
                </div>
            )}
        </div>
    );
};
